import { anyLeague } from "../../../classificators/league/index.js";
import { RankingStat } from "../../../entities/index.js";
import { BattedBallType, BattingRankingStats, PitchingRankingStats, DataSection, ResultDataNew, TaggingData, RankingScopeContext } from "../../../enums/index.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceBeforePitchType from "../../../classificators/resultsSlice/resultsSliceBeforePitchType.js";
import RankingStatCalculatorBase from "../RankingStatCalculatorBase.js";
import ContextualFilterBase from "../../../filters/contextual/ContextualFilterBase.js";
import ClassifiedAsMultipleOutsFilter from "../../../filters/contextual/ClassifiedAsMultipleOutsFilter.js";

class NewGroundIntoDoublePlayCalculator extends RankingStatCalculatorBase {
    constructor(
        context, rankingStatCaseVerifier, numberFormatter, multipleOutsFilter
    ) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(multipleOutsFilter) !== 'undefined' && !(multipleOutsFilter instanceof ContextualFilterBase)) {
            throw new TypeError('multipleOutsFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            [
                context === RankingScopeContext.Pitching.key 
                    ? PitchingRankingStats.GroundIntoDoublePlays
                    : BattingRankingStats.GroundIntoDoublePlays
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key
                        ? pitchingScopeContext
                        : battingScopeContext,
                    resultsSliceBeforePitchType
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.multipleOutsFilter = multipleOutsFilter || new ClassifiedAsMultipleOutsFilter();
        this.context = context;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getSmallestSlice();
        const multiOutsResults = this.filterResults(results, session, league);

        let gidp = 0;
        
        for (let r = 0; r < multiOutsResults.length; r++) {
            const result = multiOutsResults[r];
            
            if (
                result && result[ResultDataNew.Data.key]
                && result[ResultDataNew.Data.key][DataSection.Tagging.key]
            ) {
                if (!result[ResultDataNew.Data.key][DataSection.Tagging.key].pitch) {
                    continue;
                }
                if (!result[ResultDataNew.Data.key][DataSection.Scoring.key]) {
                    continue;
                }

                const battedBallType = result[ResultDataNew.Data.key][DataSection.Tagging.key].pitch[TaggingData.BattedBallType.key];
                if (battedBallType !== BattedBallType.GroundBall.key) {
                    continue;
                }

                gidp++;
            }
        }

        // now include hits and total bases
        if (gidp > 0) {
            const gidpParam = this.supportedParams[0];
            return RankingStat.fromParameter(
                gidpParam, this.numberFormatter.format(gidp), gidp
            );
        }

        return null;
    }

    filterResults(results, session, league) {
        return this.multipleOutsFilter.filter(results, session, league);
    }
}

export default NewGroundIntoDoublePlayCalculator;